/**
 * Sandbox Import Function
 * 
 * Imports a .gbox file and restores the sandbox state
 */

import type { SandboxSystems, SandboxSystemFile, SandboxFolder } from '../templates/types';
import type { StoredAsset } from '../assets/types';
import { createEmptyFolder } from '../templates/types';
import type { GboxClient, GboxFolder, GboxFile, GboxRunbook } from '@gotti/sandbox-shared';
import { readGottiRunbooks } from '@gotti/sandbox-shared';
import { fromSavedProject, migrateV1Project, toV1View, type ProjectTree } from '@gotti/sandbox-core';
import {
  GBOX_EXTENSION,
  parseGboxFile,
  type GboxDocument,
} from './gboxFormat';

/**
 * Result of importing a .gbox file
 */
export interface ImportResult {
  success: boolean;
  /** The project the file holds (`projectOfDocument`). */
  project?: ProjectTree;
  meta?: GboxDocument['meta'];
  config?: GboxDocument['config'];
  clients?: GboxClient[];
  assets?: StoredAsset[];
  runbooks?: GboxRunbook[];
  errors: string[];
  warnings: string[];
}

/**
 * Convert GboxFile to SandboxSystemFile
 */
function gboxFileToSystemFile(file: GboxFile): SandboxSystemFile {
  return {
    name: file.name,
    code: file.code,
  };
}

/**
 * Convert a GboxFolder to SandboxFolder format.
 *
 * Handles both the current format (`rootFiles` / `subfolders`) and the legacy
 * v1 format where each folder had direct keys: `api` (single file object),
 * `systems`, `components`, `entities` (arrays of files).
 */
function convertFolder(gboxFolder: GboxFolder): SandboxFolder {
  const folder = createEmptyFolder();

  // Current format — has `rootFiles` key
  if (Array.isArray((gboxFolder as any).rootFiles)) {
    folder.rootFiles = (gboxFolder.rootFiles || []).map(gboxFileToSystemFile);
    for (const [subfolderName, files] of Object.entries(gboxFolder.subfolders || {})) {
      folder.subfolders[subfolderName] = files.map(gboxFileToSystemFile);
    }
    return folder;
  }

  // Legacy format — migrate from flat keys (api, systems, components, entities, …)
  const raw = gboxFolder as any;
  const knownSubfolders = ['systems', 'components', 'entities', '__tests__'];

  for (const key of Object.keys(raw)) {
    const value = raw[key];
    if (knownSubfolders.includes(key) && Array.isArray(value)) {
      // Array of files → subfolder
      if (value.length > 0) {
        folder.subfolders[key] = value.map(gboxFileToSystemFile);
      }
    } else if (value && typeof value === 'object' && typeof value.code === 'string') {
      // Single file object (e.g. `api: { name, code }`) → root file
      folder.rootFiles.push(gboxFileToSystemFile(value as GboxFile));
    }
  }

  return folder;
}

/**
 * The project a document holds: its `project` section — what this site writes; a malformed one, or one a newer site
 * wrote in a format this one does not know, fails naming why rather than opening part of it — or, for a document with
 * no section (an older site's, or a `null` one), its v1 `files` migrated.
 */
export function projectOfDocument(doc: GboxDocument): ProjectTree {
  if (doc.project != null) return fromSavedProject(doc.project);
  const { files } = doc;
  return migrateV1Project(
    { client: convertFolder(files.client), server: convertFolder(files.server), shared: convertFolder(files.shared) },
    { name: doc.meta?.name || 'Untitled Sandbox' },
  );
}

/** A document's v1 view — what the runner and the AI read. */
export function gboxFilesToSystems(doc: GboxDocument): SandboxSystems {
  const project = projectOfDocument(doc);
  return toV1View(project.files, project.generated, project.folders) as SandboxSystems;
}

/** Pull inline assets (if any) out of a parsed gbox document. */
export function extractAssetsFromGbox(doc: GboxDocument): StoredAsset[] {
  const inline = (doc.extensions as { assetsInline?: StoredAsset[] } | undefined)?.assetsInline;
  return Array.isArray(inline) ? inline : [];
}

/**
 * Import a .gbox file from a File object (from file input)
 */
export async function importGboxFile(file: File): Promise<ImportResult> {
  // Validate file extension
  if (!file.name.endsWith(GBOX_EXTENSION)) {
    return {
      success: false,
      errors: [`Invalid file type. Expected ${GBOX_EXTENSION} file.`],
      warnings: [],
    };
  }

  try {
    // Read file content
    const content = await file.text();
    
    // Parse and validate
    const validation = parseGboxFile(content);
    
    if (!validation.valid || !validation.document) {
      return {
        success: false,
        errors: validation.errors,
        warnings: validation.warnings,
      };
    }

    const project = projectOfDocument(validation.document);

    return {
      success: true,
      project,
      meta: validation.document.meta,
      config: validation.document.config,
      clients: validation.document.clients,
      assets: extractAssetsFromGbox(validation.document),
      runbooks: readGottiRunbooks(validation.document),
      errors: [],
      warnings: validation.warnings,
    };
  } catch (err) {
    return {
      success: false,
      errors: [`Failed to read file: ${err instanceof Error ? err.message : 'Unknown error'}`],
      warnings: [],
    };
  }
}

/**
 * Open a file picker dialog and import a .gbox file
 */
export function openImportDialog(): Promise<ImportResult> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = GBOX_EXTENSION;
    
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        resolve({
          success: false,
          errors: ['No file selected'],
          warnings: [],
        });
        return;
      }

      const result = await importGboxFile(file);
      resolve(result);
    };

    input.oncancel = () => {
      resolve({
        success: false,
        errors: ['Import cancelled'],
        warnings: [],
      });
    };

    // Trigger file picker
    input.click();
  });
}
